import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, ArrowLeft, MapPin, Phone, User, Download, Share2 } from 'lucide-react';
import { downloadInvoice, shareInvoiceViaWhatsApp } from '@/utils/invoiceGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      toast.error('Please login to view order details');
      navigate('/login');
      return;
    }
    fetchOrderDetail();
  }, [user, id]);

  const fetchOrderDetail = async () => {
    try {
      const response = await axios.get(`${API}/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Order not found');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success';
      case 'pending':
        return 'bg-amber-500';
      case 'delivered':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/orders')}
            className="mb-6"
            data-testid="back-to-orders-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Orders
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-accent mb-2" data-testid="order-detail-id">
                      Order #{order.id.substring(0, 8)}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Placed on {new Date(order.created_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge className={`${getStatusColor(order.order_status)} text-white text-lg px-4 py-1`} data-testid="order-detail-status">
                      {order.order_status}
                    </Badge>
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          try {
                            await downloadInvoice(order);
                            toast.success('Invoice downloaded successfully!');
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error(error.message || 'Failed to download invoice');
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        data-testid="download-invoice-button"
                      >
                        <Download className="h-4 w-4" />
                        Download Invoice
                      </Button>
                      <Button
                        onClick={() => {
                          try {
                            shareInvoiceViaWhatsApp(order);
                            toast.success('Opening WhatsApp...');
                          } catch (error) {
                            console.error('Share error:', error);
                            toast.error(error.message || 'Failed to share invoice');
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        data-testid="share-invoice-button"
                      >
                        <Share2 className="h-4 w-4" />
                        Share
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-accent">Order Items</h2>
                  {order.items?.map((item) => (
                    <div key={item.product_id} className="flex gap-4 pb-4 border-b border-amber-100" data-testid={`order-item-${item.product_id}`}>
                      <img
                        src={item.product?.image}
                        alt={item.product?.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-accent">{item.product?.name}</h3>
                        {item.product?.weight && (
                          <p className="text-sm text-muted-foreground">{item.product.weight}</p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">
                          Quantity: {item.quantity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-primary">
                          ₹{(item.product?.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-accent mb-4">Delivery Information</h2>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <User className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium" data-testid="order-detail-name">{order.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium" data-testid="order-detail-phone">{order.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Address</p>
                      <p className="font-medium" data-testid="order-detail-address">{order.delivery_address}</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-lg font-semibold text-accent mb-4">Payment Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>₹{(order.subtotal || order.total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Delivery</span>
                    {order.delivery_type === 'pickup' ? (
                      <span className="text-success">Store Pickup</span>
                    ) : order.delivery_charge > 0 ? (
                      <span>₹{order.delivery_charge.toFixed(2)}</span>
                    ) : (
                      <span className="text-success">FREE</span>
                    )}
                  </div>
                  {order.discount > 0 && (
                    <div className="flex justify-between text-success">
                      <span>Discount</span>
                      <span>-₹{order.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-amber-100 pt-3 flex justify-between text-lg font-bold text-accent">
                    <span>Total</span>
                    <span data-testid="order-detail-total">₹{order.total?.toFixed(2)}</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-amber-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Payment Status</span>
                      <Badge className={`${order.payment_status === 'paid' ? 'bg-success' : 'bg-amber-500'} text-white`} data-testid="order-detail-payment-status">
                        {order.payment_status}
                      </Badge>
                    </div>
                    {order.payment_id && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Payment ID: {order.payment_id}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderDetail;
